import type {
  AgentActionId,
  AgentSourceId,
  ContactMethodId,
  LandingCategoryId,
} from '@/lib/landing-v2/content'

export type Landing2Locale = 'en' | 'hy'

export type WorkflowNodeLabels = { label: string; short: string }

export type Landing2Copy = {
  nav: {
    cta: string
    ctaShort: string
    language: string
    persianSite: string
    toLight: string
    toDark: string
    home: string
  }
  hero: {
    lineOne: string
    lineTwo: string
    lead: string
    note: string
    ctaSecondary: string
    runtime: {
      title: string
      status: string
      inputLabel: string
      problems: [string, string, string]
      stages: [
        { label: string; status: string },
        { label: string; status: string },
        { label: string; status: string },
      ]
    }
  }
  discovery: {
    label: string
    title: string
    titleEmphasis: string
    subtitle: string
    continue: string
    freeTextPlaceholder: string
    stages: [string, string, string, string]
    selectedCount: string
  }
  questions: {
    label: string
    title: string
    subtitle: string
    progress: string
    next: string
    finish: string
    back: string
    escape: string
    freeTextPlaceholder: string
  }
  workflow: {
    label: string
    title: string
    subtitle: string
    basedOn: string
    kinds: { source: string; agent: string; step: string; output: string }
    live: string
  }
  modules: {
    label: string
    title: string
    subtitle: string
    inputs: string
    output: string
    status: string
    agents: { label: string; title: string; body: string; cta: string }
  }
  contact: {
    label: string
    title: string
    subtitle: string
    submit: string
    sending: string
    supporting: string
    methods: Record<ContactMethodId, { label: string; placeholder: string }>
    phoneCountry: string
    errors: Record<'required' | 'telegram' | 'phone' | 'email' | 'submit', string>
    successLabel: string
    completeTitle: string
    completeBody: string
    partialTitle: string
    partialBody: string
    edit: string
  }
  agent: {
    label: string
    title: string
    body: string
    emphasis: string
    phases: [string, string, string]
    sourcesLabel: string
    actionsLabel: string
    coreLabel: string
    sources: Record<AgentSourceId, string>
    actions: Record<AgentActionId, string>
  }
  principles: {
    label: string
    title: string
    subtitle: string
    items: [
      { title: string; body: string },
      { title: string; body: string },
      { title: string; body: string },
    ]
  }
  process: {
    label: string
    title: string
    subtitle: string
    steps: [
      { title: string; body: string },
      { title: string; body: string },
      { title: string; body: string },
      { title: string; body: string },
    ]
  }
  closing: { title: string; body: string }
  footer: { tagline: string; copyright: string }
  categories: Record<LandingCategoryId, { label: string; hint: string }>
  categoryOtherPlaceholder: string
  questionText: Record<string, string>
  options: Record<string, string>
  workflows: Record<
    LandingCategoryId,
    [
      WorkflowNodeLabels,
      WorkflowNodeLabels,
      WorkflowNodeLabels,
      WorkflowNodeLabels,
      WorkflowNodeLabels,
    ]
  >
  modulesContent: Record<
    LandingCategoryId,
    {
      tag: string
      title: string
      benefit: string
      inputs: [string, string, string]
      output: string
    }
  >
}

const en: Landing2Copy = {
  nav: {
    cta: 'Find my automation',
    ctaShort: 'Find automation',
    language: 'Language',
    persianSite: 'Persian site',
    toLight: 'Switch to light theme',
    toDark: 'Switch to dark theme',
    home: 'Back to top',
  },
  hero: {
    lineOne: 'You bring the problem.',
    lineTwo: 'We find the automation.',
    lead: "Tell us what's slowing you down. We'll find what can be automated with Atirame.",
    note: "Not sure what can be automated? That's exactly what we're here for.",
    ctaSecondary: 'See what we automate',
    runtime: {
      title: 'atirame / runtime',
      status: 'live',
      inputLabel: 'problem',
      problems: [
        'We check competitor prices by hand every morning.',
        'The same customer questions, all day, every day.',
        'Our reports take two days to pull together.',
      ],
      stages: [
        { label: 'AI agent', status: 'assigned' },
        { label: 'Automation', status: 'built' },
        { label: 'Result', status: 'delivered' },
      ],
    },
  },
  discovery: {
    label: 'Discovery',
    title: 'What is taking too much time or money in your business?',
    titleEmphasis: 'time or money',
    subtitle: "Select one or more areas — we'll ask a few quick questions.",
    continue: 'Continue',
    freeTextPlaceholder: 'Tell us a little more…',
    stages: ['Area', 'Questions', 'Contact', 'Done'],
    selectedCount: '{count} selected',
  },
  questions: {
    label: 'Questions',
    title: "Let's make the opportunity clearer.",
    subtitle: 'Your answers shape a workflow around how your business already works.',
    progress: 'Question {current} of {total}',
    next: 'Next question',
    finish: 'See my automation',
    back: 'Back',
    escape: 'Take it from here',
    freeTextPlaceholder: 'Tell us a little more…',
  },
  workflow: {
    label: 'Your workflow, reimagined',
    title: "Here's how Atirame could help.",
    subtitle: "Based on what you told us — we'll refine this when we talk.",
    basedOn: 'Based on',
    kinds: { source: 'source', agent: 'agent', step: 'step', output: 'output' },
    live: 'running',
  },
  modules: {
    label: 'Possibilities',
    title: 'What can Atirame automate?',
    subtitle: 'Four automations that start from tools you already use.',
    inputs: 'Inputs',
    output: 'Automated outcome',
    status: 'automated',
    agents: {
      label: 'Agents',
      title: 'Try the agents yourself',
      body: 'Open the Atirame workspace to set up your chatbot, manage visitor chats, or play Mafia against AI players.',
      cta: 'Open Agents',
    },
  },
  contact: {
    label: 'Contact',
    title: "Let's find what you can automate.",
    subtitle:
      "Tell Atirame what's slowing your business down. We'll come back with an automation idea.",
    submit: 'Send to Atirame',
    sending: 'Sending…',
    supporting:
      "We'll review what you've told us and contact you with an automation idea.",
    methods: {
      email: { label: 'Email', placeholder: 'you@company.com' },
      telegram: { label: 'Telegram', placeholder: '@username' },
      phone: { label: 'Phone', placeholder: '555 000 0000' },
    },
    phoneCountry: 'Country code',
    errors: {
      required: 'Please enter your contact details.',
      telegram: 'Please enter a valid Telegram username.',
      phone: 'Please enter a valid phone number for the selected country.',
      email: 'Please enter a valid email address.',
      submit: 'Something went wrong. Please try again.',
    },
    successLabel: 'Message received',
    completeTitle: "We've got it.",
    completeBody:
      "We'll review your workflow and get back to you with ideas for automating it with Atirame.",
    partialTitle: "We've saved what you shared.",
    partialBody:
      "We'll review it and get back to you with ideas for automating your workflow — no need to answer everything now.",
    edit: 'Edit contact details',
  },
  agent: {
    label: 'The agent behind the automation',
    title: 'AI agents that work behind the scenes.',
    body: 'Behind every automation is an AI agent. Atirame uses AI agents to understand your workflow, connect your tools, and handle tasks automatically.',
    emphasis: "They don't just answer questions — they do the work.",
    phases: ['Understand', 'Automate', 'Act'],
    sourcesLabel: 'Your tools',
    actionsLabel: 'Work completed',
    coreLabel: 'Atirame agent',
    sources: {
      website: 'Your website',
      store: 'Your store',
      inbox: 'Inbox & chat',
      sheets: 'Spreadsheets',
    },
    actions: {
      reply_sent: 'Reply sent',
      price_updated: 'Price updated',
      report_delivered: 'Report delivered',
      lead_scored: 'Lead scored',
    },
  },
  principles: {
    label: 'Built differently',
    title: 'Why Atirame?',
    subtitle: 'We build AI-powered automation around the way your business already works.',
    items: [
      {
        title: 'Your workflow',
        body: 'We adapt to the way your business already works.',
      },
      {
        title: 'Your tools',
        body: 'We connect with the tools you already use.',
      },
      {
        title: 'Your needs',
        body: 'Every automation is designed around your specific business.',
      },
    ],
  },
  process: {
    label: 'A simple process',
    title: 'How it works',
    subtitle: 'From problem to working automation, in four steps.',
    steps: [
      { title: 'Tell us', body: "Tell us what's taking too much time or money." },
      {
        title: 'We understand',
        body: 'We look at your workflow and identify what can be automated.',
      },
      { title: 'We build', body: 'We design and implement the solution with Atirame.' },
      { title: 'It works', body: 'Your AI-powered workflow handles the repetitive work.' },
    ],
  },
  closing: {
    title: 'Still not sure what to automate?',
    body: "That's the part we're good at. Start with the problem — we'll find the rest.",
  },
  footer: {
    tagline: "Custom automation built on Atirame's agentic platform",
    copyright: '© {year} Atirame. All rights reserved.',
  },
  categories: {
    ecommerce: { label: 'E-commerce', hint: 'Prices, products & competitors' },
    customer_support: {
      label: 'Customer Support',
      hint: 'Repetitive questions & requests',
    },
    sales: { label: 'Sales & Leads', hint: 'Finding, qualifying & following up' },
    data_reporting: {
      label: 'Data & Reporting',
      hint: 'Reports, analysis & scattered data',
    },
    marketing: { label: 'Marketing', hint: 'Content & social media' },
    operations: { label: 'Operations', hint: 'Repetitive manual tasks' },
    other: { label: 'Something else', hint: 'Describe your own challenge' },
  },
  categoryOtherPlaceholder: 'Describe your problem in your own words…',
  questionText: {
    'shared.frequency': 'How often does this happen?',
    'ecommerce.time_spent': 'What takes most of your time?',
    'ecommerce.current_process': 'How do you do it today?',
    'customer_support.support_volume': 'What is your biggest support challenge?',
    'customer_support.current_process': 'How do you handle support today?',
    'sales.sales_challenge': 'What slows down your sales process?',
    'sales.current_process': 'How do you manage sales today?',
    'data_reporting.reporting_challenge': 'What is hardest about your reporting?',
    'data_reporting.current_process': 'How do you build reports today?',
    'marketing.marketing_challenge': 'What takes most of your marketing time?',
    'marketing.current_process': 'How do you manage marketing today?',
    'operations.operations_challenge': 'Which manual task costs you the most?',
    'operations.current_process': 'How is this handled today?',
    'other.problem_description': 'Tell us about the problem you want to solve.',
    'other.current_process': 'How do you handle it today?',
  },
  options: {
    competitor_prices: 'Checking competitor prices',
    updating_products: 'Updating products',
    answering_customers: 'Answering customers',
    creating_reports: 'Creating reports',
    high_volume: 'High volume of requests',
    repetitive_questions: 'Repetitive questions',
    slow_response: 'Slow response times',
    after_hours: 'After-hours coverage',
    finding_leads: 'Finding leads',
    qualifying_leads: 'Qualifying leads',
    follow_up: 'Following up with prospects',
    crm_updates: 'Updating CRM records',
    manual_reports: 'Building reports manually',
    scattered_data: 'Data scattered across tools',
    slow_insights: 'Slow to get insights',
    error_prone: 'Errors in manual work',
    content_creation: 'Creating content',
    social_posting: 'Posting on social media',
    campaign_tracking: 'Tracking campaigns',
    audience_research: 'Researching audiences',
    data_entry: 'Manual data entry',
    approvals: 'Manual approvals',
    inventory: 'Inventory updates',
    scheduling: 'Scheduling & coordination',
    repetitive_workflow: 'I have a repetitive workflow',
    team_overloaded: 'My team is overloaded',
    data_hard_to_use: 'Our data is hard to use',
    manually: 'Manually',
    spreadsheets: 'Excel / Google Sheets',
    website: 'Website / WooCommerce',
    multiple_tools: 'Multiple tools',
    helpdesk: 'Help desk software',
    chat_widget: 'Chat widget',
    email_only: 'Email only',
    crm: 'CRM system',
    dashboards: 'Dashboards',
    manual_exports: 'Manual exports',
    scheduling_tools: 'Scheduling tools',
    agencies: 'Agencies / freelancers',
    erp: 'ERP / internal system',
    not_handled: 'Not handled yet',
    daily: 'Every day',
    several_weekly: 'Several times a week',
    occasionally: 'Occasionally',
    other: 'Something else',
  },
  workflows: {
    ecommerce: [
      { label: 'Your store', short: 'Store' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Competitor monitoring', short: 'Competitors' },
      { label: 'Price analysis', short: 'Prices' },
      { label: 'Daily report', short: 'Report' },
    ],
    customer_support: [
      { label: 'Customer message', short: 'Message' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Your knowledge base', short: 'Knowledge' },
      { label: 'Answer drafted', short: 'Answer' },
      { label: 'Replied or escalated', short: 'Replied' },
    ],
    sales: [
      { label: 'New lead', short: 'Lead' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Company research', short: 'Research' },
      { label: 'Qualification', short: 'Qualified' },
      { label: 'CRM updated', short: 'CRM' },
    ],
    data_reporting: [
      { label: 'Your data', short: 'Data' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Analysis', short: 'Analysis' },
      { label: 'Insights', short: 'Insights' },
      { label: 'Report delivered', short: 'Report' },
    ],
    marketing: [
      { label: 'Content plan', short: 'Plan' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Drafts & assets', short: 'Drafts' },
      { label: 'Scheduling', short: 'Schedule' },
      { label: 'Published & tracked', short: 'Published' },
    ],
    operations: [
      { label: 'Manual task', short: 'Task' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Automation', short: 'Automation' },
      { label: 'Validation', short: 'Checked' },
      { label: 'Completed', short: 'Done' },
    ],
    other: [
      { label: 'Your workflow', short: 'Workflow' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Understand', short: 'Understand' },
      { label: 'Automate', short: 'Automate' },
      { label: 'Result', short: 'Result' },
    ],
  },
  modulesContent: {
    ecommerce: {
      tag: 'E-commerce',
      title: 'Competitor tracking',
      benefit: 'Monitor competitors prices automatically',
      inputs: ['Your store', 'Competitor pages', 'Your price list'],
      output: 'A daily price report, before you open your laptop',
    },
    customer_support: {
      tag: 'Customer Support',
      title: 'AI assistant',
      benefit: 'Handle repetitive customer questions',
      inputs: ['Website chat', 'Your FAQ & docs', 'Order status'],
      output: 'Answered in seconds, escalated when it matters',
    },
    sales: {
      tag: 'Sales',
      title: 'AI sales agent',
      benefit: 'Research and qualify leads automatically',
      inputs: ['Inbound forms', 'Public company data', 'Your CRM'],
      output: 'A qualified lead with notes, already in your CRM',
    },
    data_reporting: {
      tag: 'Data & Reporting',
      title: 'AI reporting',
      benefit: 'Turn scattered data into useful reports',
      inputs: ['Spreadsheets', 'Dashboards', 'Manual exports'],
      output: 'One report, every morning, in one place',
    },
    marketing: {
      tag: 'Marketing',
      title: 'Content automation',
      benefit: 'Keep content moving without the manual work',
      inputs: ['Your content plan', 'Brand guidelines', 'Social accounts'],
      output: 'Drafts ready to review, scheduled and tracked',
    },
    operations: {
      tag: 'Operations',
      title: 'Task automation',
      benefit: 'Take repetitive manual tasks off your team',
      inputs: ['Internal systems', 'Spreadsheets', 'Approval rules'],
      output: 'The task runs itself, and flags what needs a human',
    },
    other: {
      tag: 'Your workflow',
      title: 'Custom automation',
      benefit: 'Turn a repetitive process into an automation',
      inputs: ['Your process', 'Your tools', 'Your rules'],
      output: 'A workflow that runs without being chased',
    },
  },
}

const hy: Landing2Copy = {
  nav: {
    cta: 'Գտնել իմ ավտոմատացումը',
    ctaShort: 'Ավտոմատացում',
    language: 'Լեզու',
    persianSite: 'Պարսկերեն կայք',
    toLight: 'Անցնել բաց թեմայի',
    toDark: 'Անցնել մուգ թեմայի',
    home: 'Վերև',
  },
  hero: {
    lineOne: 'Դուք բերում եք խնդիրը։',
    lineTwo: 'Մենք գտնում ենք ավտոմատացումը։',
    lead: 'Պատմեք, թե ինչն է ձեզ դանդաղեցնում։ Մենք կգտնենք, թե ինչ կարելի է ավտոմատացնել Atirame-ով։',
    note: 'Չգիտե՞ք, թե ինչ կարելի է ավտոմատացնել։ Դրա համար էլ մենք այստեղ ենք։',
    ctaSecondary: 'Տեսնել, թե ինչ ենք ավտոմատացնում',
    runtime: {
      title: 'atirame / runtime',
      status: 'ակտիվ',
      inputLabel: 'խնդիր',
      problems: [
        'Ամեն առավոտ ձեռքով ստուգում ենք մրցակիցների գները։',
        'Նույն հարցերը՝ ամբողջ օրը, ամեն օր։',
        'Հաշվետվություն պատրաստելը երկու օր է տանում։',
      ],
      stages: [
        { label: 'ԱԲ գործակալ', status: 'նշանակված' },
        { label: 'Ավտոմատացում', status: 'կառուցված' },
        { label: 'Արդյունք', status: 'առաքված' },
      ],
    },
  },
  discovery: {
    label: 'Հայտնաբերում',
    title: 'Ինչն է ձեր բիզնեսում չափազանց շատ ժամանակ կամ գումար վատնում?',
    titleEmphasis: 'ժամանակ կամ գումար',
    subtitle: 'Ընտրեք մեկ կամ մի քանի ոլորտ — մենք մի քանի հարց կտանք։',
    continue: 'Շարունակել',
    freeTextPlaceholder: 'Պատմեք մի փոքր ավելին…',
    stages: ['Ոլորտ', 'Հարցեր', 'Կոնտակտ', 'Պատրաստ'],
    selectedCount: 'ընտրված է {count}',
  },
  questions: {
    label: 'Հարցեր',
    title: 'Դարձնենք հնարավորությունը ավելի պարզ։',
    subtitle: 'Ձեր պատասխանները ձևավորում են աշխատանքային հոսք ձեր բիզնեսի շուրջ։',
    progress: 'Հարց {current} / {total}',
    next: 'Հաջորդ հարցը',
    finish: 'Տեսնել իմ ավտոմատացումը',
    back: 'Հետ',
    escape: 'Շարունակեք դուք',
    freeTextPlaceholder: 'Պատմեք մի փոքր ավելին…',
  },
  workflow: {
    label: 'Ձեր հոսքը՝ վերաիմաստավորված',
    title: 'Ահա թե ինչպես Atirame-ը կարող է օգնել։',
    subtitle: 'Ըստ ձեր պատմածի — կճշտենք զրույցի ժամանակ։',
    basedOn: 'Հիմքում՝',
    kinds: { source: 'աղբյուր', agent: 'գործակալ', step: 'քայլ', output: 'արդյունք' },
    live: 'աշխատում է',
  },
  modules: {
    label: 'Հնարավորություններ',
    title: 'Ինչ կարող է ավտոմատացնել Atirame-ը?',
    subtitle: 'Չորս ավտոմատացում, որոնք սկսվում են ձեր արդեն օգտագործվող գործիքներից։',
    inputs: 'Մուտքեր',
    output: 'Ավտոմատ արդյունք',
    status: 'ավտոմատացված',
    agents: {
      label: 'Գործակալներ',
      title: 'Փորձեք գործակալները ինքներդ',
      body: 'Բացեք Atirame-ի աշխատատարածքը՝ չաթբոտը կարգավորելու, այցելուների զրույցները կառավարելու կամ AI խաղացողների դեմ Մաֆիա խաղալու համար։',
      cta: 'Բացել գործակալները',
    },
  },
  contact: {
    label: 'Կապ',
    title: 'Գտնենք, թե ինչ կարող եք ավտոմատացնել։',
    subtitle:
      'Պատմեք Atirame-ին, թե ինչն է դանդաղեցնում ձեր բիզնեսը։ Մենք կվերադառնանք ավտոմատացման գաղափարով։',
    submit: 'Ուղարկել Atirame-ին',
    sending: 'Ուղարկվում է…',
    supporting: 'Մենք կվերանայենք ձեր պատասխանները և կկապվենք ավտոմատացման գաղափարով։',
    methods: {
      email: { label: 'Էլ. փոստ', placeholder: 'you@company.com' },
      telegram: { label: 'Telegram', placeholder: '@օգտանուն' },
      phone: { label: 'Հեռախոս', placeholder: '00 000000' },
    },
    phoneCountry: 'Երկրի կոդ',
    errors: {
      required: 'Խնդրում ենք մուտքագրել կոնտակտը։',
      telegram: 'Մուտքագրեք վավեր Telegram օգտանուն։',
      phone: 'Մուտքագրեք ընտրված երկրի վավեր հեռախոսահամար։',
      email: 'Մուտքագրեք վավեր էլ. հասցե։',
      submit: 'Սխալ է տեղի ունեցել։ Խնդրում ենք կրկին փորձել։',
    },
    successLabel: 'Հաղորդագրությունը ստացված է',
    completeTitle: 'Ստացանք։',
    completeBody:
      'Մենք կվերանայենք ձեր աշխատանքային հոսքը և կկապվենք ավտոմատացման գաղափարներով։',
    partialTitle: 'Պահպանեցինք ձեր պատասխանները։',
    partialBody:
      'Մենք կվերանայենք դրանք և կկապվենք — պետք չէ հիմա ամեն ինչին պատասխանել։',
    edit: 'Փոխել կոնտակտը',
  },
  agent: {
    label: 'Գործակալը ավտոմատացման հետևում',
    title: 'ԱԲ գործակալներ, որոնք աշխատում են հետին պլանում։',
    body: 'Յուրաքանչյուր ավտոմատացման հետևում ԱԲ գործակալ է։ Atirame-ը հասկանում է ձեր հոսքը, միացնում գործիքները և կատարում աշխատանքը։',
    emphasis: 'Նրանք ոչ միայն պատասխանում են հարցերին — նրանք կատարում են աշխատանքը։',
    phases: ['Հասկանալ', 'Ավտոմատացնել', 'Գործել'],
    sourcesLabel: 'Ձեր գործիքները',
    actionsLabel: 'Կատարված աշխատանք',
    coreLabel: 'Atirame գործակալ',
    sources: {
      website: 'Ձեր կայքը',
      store: 'Ձեր խանութը',
      inbox: 'Փոստ և չաթ',
      sheets: 'Աղյուսակներ',
    },
    actions: {
      reply_sent: 'Պատասխանն ուղարկված է',
      price_updated: 'Գինը թարմացված է',
      report_delivered: 'Հաշվետվությունն առաքված է',
      lead_scored: 'Լիդը գնահատված է',
    },
  },
  principles: {
    label: 'Կառուցված այլ կերպ',
    title: 'Ինչու Atirame?',
    subtitle: 'Մենք կառուցում ենք ավտոմատացում ձեր բիզնեսի արդեն գործող եղանակի շուրջ։',
    items: [
      {
        title: 'Ձեր հոսքը',
        body: 'Մենք հարմարվում ենք ձեր բիզնեսի արդեն գործող եղանակին։',
      },
      {
        title: 'Ձեր գործիքները',
        body: 'Մենք միանում ենք այն գործիքներին, որոնք արդեն օգտագործում եք։',
      },
      {
        title: 'Ձեր կարիքները',
        body: 'Յուրաքանչյուր ավտոմատացում նախագծված է հատուկ ձեր բիզնեսի համար։',
      },
    ],
  },
  process: {
    label: 'Պարզ գործընթաց',
    title: 'Ինչպես է աշխատում',
    subtitle: 'Խնդրից մինչև աշխատող ավտոմատացում՝ չորս քայլով։',
    steps: [
      {
        title: 'Պատմեք',
        body: 'Ասեք, թե ինչն է չափազանց շատ ժամանակ կամ գումար վատնում։',
      },
      {
        title: 'Հասկանում ենք',
        body: 'Վերլուծում ենք ձեր հոսքը և գտնում, թե ինչը կարելի է ավտոմատացնել։',
      },
      {
        title: 'Կառուցում ենք',
        body: 'Նախագծում և իրականացնում ենք լուծումը Atirame-ով։',
      },
      {
        title: 'Աշխատում է',
        body: 'Ավտոմատացված հոսքը կատարում է կրկնվող աշխատանքը։',
      },
    ],
  },
  closing: {
    title: 'Դեռ չգիտե՞ք, թե ինչը ավտոմատացնել',
    body: 'Հենց դա է մեր գործը։ Սկսեք խնդրից — մնացածը մենք կգտնենք։',
  },
  footer: {
    tagline: 'Հատուկ ավտոմատացում Atirame-ի գործակալային հարթակի վրա',
    copyright: '© {year} Atirame։ Բոլոր իրավունքները պաշտպանված են։',
  },
  categories: {
    ecommerce: { label: 'Էլեկտրոնային առևտուր', hint: 'Գներ, ապրանքներ և մրցակիցներ' },
    customer_support: {
      label: 'Հաճախորդների աջակցություն',
      hint: 'Կրկնվող հարցեր և խնդրանքներ',
    },
    sales: { label: 'Վաճառք և լիդեր', hint: 'Գտնել, գնահատել և հետևել' },
    data_reporting: {
      label: 'Տվյալներ և հաշվետվություն',
      hint: 'Հաշվետվություններ և ցրված տվյալներ',
    },
    marketing: { label: 'Մարքեթինգ', hint: 'Բովանդակություն և սոցիալական մեդիա' },
    operations: { label: 'Գործառնություններ', hint: 'Կրկնվող ձեռքի աշխատանքներ' },
    other: { label: 'Այլ բան', hint: 'Նկարագրեք ձեր խնդիրը' },
  },
  categoryOtherPlaceholder: 'Նկարագրեք ձեր խնդիրը ձեր բառերով…',
  questionText: {
    'shared.frequency': 'Որքան հաճախ է դա տեղի ունենում?',
    'ecommerce.time_spent': 'Ինչի վրա է գնում ձեր ժամանակի մեծ մասը?',
    'ecommerce.current_process': 'Ինչպես եք դա անում այսօր?',
    'customer_support.support_volume': 'Ո՞րն է աջակցության ամենամեծ մարտահրավերը',
    'customer_support.current_process': 'Ինչպես եք այսօր մշակում հարցումները?',
    'sales.sales_challenge': 'Ինչն է դանդաղեցնում վաճառքը?',
    'sales.current_process': 'Ինչպես եք այսօր կառավարում վաճառքը?',
    'data_reporting.reporting_challenge': 'Ինչն է ամենադժվարը հաշվետվություններում?',
    'data_reporting.current_process': 'Ինչպես եք այսօր կազմում հաշվետվությունները?',
    'marketing.marketing_challenge': 'Ինչի վրա է գնում մարքեթինգի ժամանակի մեծ մասը?',
    'marketing.current_process': 'Ինչպես եք այսօր կառավարում մարքեթինգը?',
    'operations.operations_challenge': 'Որ ձեռքի աշխատանքն է ձեզ ամենաթանկ արժենում?',
    'operations.current_process': 'Ինչպես է դա այսօր կատարվում?',
    'other.problem_description': 'Պատմեք խնդրի մասին, որը ցանկանում եք լուծել։',
    'other.current_process': 'Ինչպես եք այսօր դա անում?',
  },
  options: {
    competitor_prices: 'Մրցակիցների գների ստուգում',
    updating_products: 'Ապրանքների թարմացում',
    answering_customers: 'Հաճախորդներին պատասխանելը',
    creating_reports: 'Հաշվետվությունների կազմում',
    high_volume: 'Հարցումների մեծ ծավալ',
    repetitive_questions: 'Կրկնվող հարցեր',
    slow_response: 'Դանդաղ պատասխաններ',
    after_hours: 'Աշխատանքային ժամերից դուրս սպասարկում',
    finding_leads: 'Լիդերի գտնում',
    qualifying_leads: 'Լիդերի գնահատում',
    follow_up: 'Հետագա կապ հաճախորդների հետ',
    crm_updates: 'CRM-ի թարմացում',
    manual_reports: 'Ձեռքով հաշվետվությունների կազմում',
    scattered_data: 'Գործիքների միջև ցրված տվյալներ',
    slow_insights: 'Դանդաղ վերլուծություն',
    error_prone: 'Սխալներ ձեռքի աշխատանքում',
    content_creation: 'Բովանդակության ստեղծում',
    social_posting: 'Սոցիալական ցանցերում հրապարակում',
    campaign_tracking: 'Քարոզարշավների հետևում',
    audience_research: 'Լսարանի հետազոտում',
    data_entry: 'Տվյալների ձեռքով մուտքագրում',
    approvals: 'Ձեռքով հաստատումներ',
    inventory: 'Պահեստի թարմացում',
    scheduling: 'Պլանավորում և համաձայնեցում',
    repetitive_workflow: 'Ունեմ կրկնվող աշխատանքային հոսք',
    team_overloaded: 'Թիմս գերծանրաբեռնված է',
    data_hard_to_use: 'Տվյալներս դժվար է օգտագործել',
    manually: 'Ձեռքով',
    spreadsheets: 'Excel / Google Sheets',
    website: 'Կայք / WooCommerce',
    multiple_tools: 'Մի քանի գործիք',
    helpdesk: 'Help desk ծրագիր',
    chat_widget: 'Չաթ վիջեթ',
    email_only: 'Միայն էլ. փոստ',
    crm: 'CRM համակարգ',
    dashboards: 'Դաշբորդներ',
    manual_exports: 'Ձեռքով արտահանումներ',
    scheduling_tools: 'Պլանավորման գործիքներ',
    agencies: 'Գործակալություններ / ֆրիլանսերներ',
    erp: 'ERP / ներքին համակարգ',
    not_handled: 'Դեռ չի կարգավորվում',
    daily: 'Ամեն օր',
    several_weekly: 'Շաբաթը մի քանի անգամ',
    occasionally: 'Երբեմն',
    other: 'Այլ բան',
  },
  workflows: {
    ecommerce: [
      { label: 'Ձեր խանութը', short: 'Խանութ' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Մրցակիցների մոնիտորինգ', short: 'Մրցակիցներ' },
      { label: 'Գների վերլուծություն', short: 'Գներ' },
      { label: 'Ամենօրյա հաշվետվություն', short: 'Հաշվետվություն' },
    ],
    customer_support: [
      { label: 'Հաճախորդի հարցում', short: 'Հարցում' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Ձեր գիտելիքի բազան', short: 'Գիտելիք' },
      { label: 'Պատասխանի նախագիծ', short: 'Պատասխան' },
      { label: 'Ուղարկված կամ փոխանցված', short: 'Ուղարկված' },
    ],
    sales: [
      { label: 'Նոր լիդ', short: 'Լիդ' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Ընկերության հետազոտում', short: 'Հետազոտում' },
      { label: 'Գնահատում', short: 'Գնահատում' },
      { label: 'CRM-ը թարմացված է', short: 'CRM' },
    ],
    data_reporting: [
      { label: 'Ձեր տվյալները', short: 'Տվյալներ' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Վերլուծություն', short: 'Վերլուծություն' },
      { label: 'Եզրակացություններ', short: 'Եզրակացություն' },
      { label: 'Հաշվետվությունն առաքված է', short: 'Հաշվետվություն' },
    ],
    marketing: [
      { label: 'Բովանդակության պլան', short: 'Պլան' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Նախագծեր և նյութեր', short: 'Նյութեր' },
      { label: 'Պլանավորում', short: 'Ժամանակացույց' },
      { label: 'Հրապարակված և չափված', short: 'Հրապարակում' },
    ],
    operations: [
      { label: 'Ձեռքի աշխատանք', short: 'Աշխատանք' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Ավտոմատացում', short: 'Ավտոմատացում' },
      { label: 'Ստուգում', short: 'Ստուգում' },
      { label: 'Ավարտված', short: 'Ավարտված' },
    ],
    other: [
      { label: 'Ձեր հոսքը', short: 'Հոսք' },
      { label: 'Atirame', short: 'Atirame' },
      { label: 'Հասկանալ', short: 'Հասկանալ' },
      { label: 'Ավտոմատացնել', short: 'Ավտոմատացնել' },
      { label: 'Արդյունք', short: 'Արդյունք' },
    ],
  },
  modulesContent: {
    ecommerce: {
      tag: 'Էլեկտրոնային առևտուր',
      title: 'Մրցակիցների հետևում',
      benefit: 'Ավտոմատ մրցակիցների մոնիտորինգ',
      inputs: ['Ձեր խանութը', 'Մրցակիցների էջերը', 'Ձեր գնացուցակը'],
      output: 'Ամենօրյա գների հաշվետվություն՝ մինչև աշխատանքի սկիզբը',
    },
    customer_support: {
      tag: 'Աջակցություն',
      title: 'ԱԲ օգնական',
      benefit: 'Կրկնվող հարցերի մշակում',
      inputs: ['Կայքի չաթը', 'Ձեր FAQ-ն և փաստաթղթերը', 'Պատվերի կարգավիճակը'],
      output: 'Պատասխան վայրկյանների ընթացքում, փոխանցում՝ երբ պետք է',
    },
    sales: {
      tag: 'Վաճառք',
      title: 'ԱԲ վաճառքի գործակալ',
      benefit: 'Լիդերի հետազոտություն և գնահատում',
      inputs: ['Մուտքային հայտերը', 'Ընկերությունների բաց տվյալները', 'Ձեր CRM-ը'],
      output: 'Գնահատված լիդ՝ նշումներով, արդեն ձեր CRM-ում',
    },
    data_reporting: {
      tag: 'Հաշվետվություն',
      title: 'ԱԲ հաշվետվություն',
      benefit: 'Տվյալներից օգտակար հաշվետվություններ',
      inputs: ['Աղյուսակները', 'Դաշբորդները', 'Ձեռքով արտահանումները'],
      output: 'Մեկ հաշվետվություն՝ ամեն առավոտ, մեկ տեղում',
    },
    marketing: {
      tag: 'Մարքեթինգ',
      title: 'Բովանդակության ավտոմատացում',
      benefit: 'Բովանդակությունը շարժման մեջ՝ առանց ձեռքի աշխատանքի',
      inputs: ['Ձեր բովանդակության պլանը', 'Բրենդի ուղեցույցը', 'Սոցիալական հաշիվները'],
      output: 'Նախագծեր՝ պատրաստ ստուգման, պլանավորված և չափված',
    },
    operations: {
      tag: 'Գործառնություններ',
      title: 'Առաջադրանքների ավտոմատացում',
      benefit: 'Կրկնվող ձեռքի աշխատանքը թիմից դուրս',
      inputs: ['Ներքին համակարգերը', 'Աղյուսակները', 'Հաստատման կանոնները'],
      output: 'Աշխատանքը կատարվում է ինքնուրույն և նշում է մարդու միջամտությունը',
    },
    other: {
      tag: 'Ձեր հոսքը',
      title: 'Հատուկ ավտոմատացում',
      benefit: 'Կրկնվող գործընթացը դարձնել ավտոմատացում',
      inputs: ['Ձեր գործընթացը', 'Ձեր գործիքները', 'Ձեր կանոնները'],
      output: 'Հոսք, որն աշխատում է առանց հիշեցնելու',
    },
  },
}

export const landing2Copy: Record<Landing2Locale, Landing2Copy> = { en, hy }

export function getLanding2Copy(locale: Landing2Locale): Landing2Copy {
  return landing2Copy[locale] ?? landing2Copy.en
}

export function formatCopy(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  )
}
